import apiEndPoints from "../../apiconstants";
import {
    buildClaimIntakeRequestHeaders,
    resolveClaimIntakeBearerToken,
    shouldUseClaimIntakeRestPaths,
    toClaimIntakeApiPath,
} from "../../claimIntakeApiConfig";
import { getValidClaimIntakeBearerToken } from "../../claimIntakeTokenUtils";
import { api, handleErrorResponse, responseArgs } from "../api";
import type { RootState } from "../../store";
import { AIWorker } from "./aiworkerTypes";

export enum categoryType {
    ANALYST = 'analyst',
    SPECIALIST = 'specialist',
    ADVISOR = 'advisor',
    AGENT = 'agent',
    ACTION = 'action',
    UXGENIE = 'uxgenie',
    MAPPER = 'mapper',
}

export enum aiworkerUsageType {
    ANALYST = 'data_logs',
    ADVISOR = 'conversations',
    AGENT = 'action_logs',
    SPECIALIST = 'circuit_logs',
}

/** Unwrap `{ status_code, data }` from fetchBaseQuery (transformResponse is not run for queryFn). */
function unwrapApiPayload(result: { data?: unknown; error?: unknown; meta?: unknown }) {
    if (result.error) return result;
    const body = result.data as responseArgs | unknown;
    const inner =
        body != null && typeof body === 'object' && 'data' in (body as object)
            ? (body as responseArgs).data
            : body;
    return { data: inner, meta: result.meta };
}

/** `/sources?source_type=…` — array or `{ sources: [...] }` depending on API version. */
export function normalizeSourceTypeActionsData(data: unknown): any[] {
    if (Array.isArray(data)) return data;
    if (data != null && typeof data === 'object') {
        const record = data as Record<string, unknown>;
        if ('data' in record && record.data !== undefined) {
            const nested = normalizeSourceTypeActionsData(record.data);
            if (nested.length > 0 || Array.isArray(record.data)) return nested;
        }
        for (const key of ['sources', 'actions', 'agents'] as const) {
            const list = record[key];
            if (Array.isArray(list)) return list;
        }
    }
    return [];
}

export const aidesApiSlice = api.injectEndpoints({
    endpoints: builder => ({
        getAiWorkersByCategory: builder.query<AIWorker[], { category: categoryType, token?: any }>({
            queryFn: async (payload, api, _extra, baseQuery) => {
                const url = `${apiEndPoints?.getAiWorkersByCategory}?category=${payload.category}`;
                const state = api.getState() as RootState;
                const useRest = shouldUseClaimIntakeRestPaths(state);
                const result = useRest
                    ? await baseQuery({
                          url: toClaimIntakeApiPath(url, true),
                          headers: buildClaimIntakeRequestHeaders(api.getState),
                      })
                    : await baseQuery({
                          url,
                          headers: {
                              ...(payload?.token?.apiToken && {
                                  Authorization: `Bearer ${payload.token.apiToken}`,
                              }),
                              ...(payload?.token?.apiKey && { 'x-api-key': payload.token.apiKey }),
                          },
                      });
                return unwrapApiPayload(result);
            },
            transformErrorResponse: handleErrorResponse,
        }),
        /**
         * Plain GET for a command-form field's declared `source: {kind:'api', endpoint}` path
         * (e.g. `/api/v1/aiworkers?category=uxgenie&sub_type=inspection`). The path and the
         * record fields to read come entirely from the agent's command json, so a new lookup
         * needs no code change here.
         */
        getCommandSourceRecords: builder.query<Record<string, any>[], string>({
            query: (endpoint) => ({ url: endpoint, method: 'GET' }),
            transformResponse: (response: responseArgs) => {
                const data = Array.isArray(response) ? response : response?.data;
                return Array.isArray(data) ? data : [];
            },
            transformErrorResponse: handleErrorResponse,
        }),
        /** Executes a command-form request declared entirely by command JSON. */
        executeCommandRequest: builder.mutation<any, { endpoint: string; method?: string; body?: unknown }>({
            query: ({ endpoint, method = 'GET', body }) => ({
                url: endpoint,
                method,
                ...(body === undefined ? {} : { body }),
            }),
            transformErrorResponse: handleErrorResponse,
        }),
        /**
         * Runs a configured integration action server side by its action `code`, so a command form
         * can reach an external service that can't be called from the browser (e.g. blocked by CORS).
         * `endpoint` / `method` come from the command json when it sets them.
         */
        executeCommandIntegration: builder.mutation<any, { code: string; payload?: unknown; endpoint?: string; method?: string }>({
            query: ({ code, payload, endpoint, method }) => ({
                url: endpoint || `${apiEndPoints?.actionIntegrations}`,
                method: method || 'POST',
                body: { code, payload: payload ?? {} },
            }),
            transformErrorResponse: handleErrorResponse,
        }),
        getAiWorkerById: builder.query<AIWorker, string>({
            query: (workerId) => `${apiEndPoints?.getAiWorkerById}/${workerId}`,
            transformResponse: (response: responseArgs) => response.data as AIWorker,
            transformErrorResponse: handleErrorResponse,
        }),
        fetchAiWorkersByCategory: builder.mutation<any, categoryType>({
            query: (payload) => ({
                url: `${apiEndPoints?.getAiWorkersByCategory}?category=${payload}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response.data as Report[],
            transformErrorResponse: handleErrorResponse,
        }),
        updateAiWorkers: builder.mutation<any, any>({
            query: ({ payLoad, aiworker_id }) => ({
                url: `${apiEndPoints?.updateAiWorkers}/${aiworker_id}`,
                method: 'PATCH',
                body: payLoad
            }),
            transformErrorResponse: handleErrorResponse,
        }),
        createAiWorkers: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: `${apiEndPoints?.createAiWorkers}`,
                method: 'POST',
                body: payLoad
            }),
            transformErrorResponse: handleErrorResponse,
        }),
        deleteAiWorkers: builder.mutation<any, string>({
            query: (aiworker_id) => ({
                url: `${apiEndPoints?.deleteAiWorkers}/${aiworker_id}`,
                method: 'DELETE'
            }),
            transformErrorResponse: handleErrorResponse,
            transformResponse: (response: responseArgs) => response.data
        }),
        circuitConnector: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: `${apiEndPoints?.circuitConnector}`,
                method: 'POST',
                body: payLoad
            }),
            transformErrorResponse: handleErrorResponse,
            transformResponse: (response: responseArgs) => response.data
        }),
        getAiWorkersBySourceType: builder.query<any, string>({
            queryFn: async (sourceType, api, _extra, baseQuery) => {
                const url = `${apiEndPoints?.getAiWorkersBySourceType}?source_type=${sourceType}`;
                const state = api.getState() as RootState;
                const useRest = shouldUseClaimIntakeRestPaths(state);
                const result = useRest
                    ? await baseQuery({
                          url: toClaimIntakeApiPath(url, true),
                          headers: buildClaimIntakeRequestHeaders(api.getState),
                      })
                    : await baseQuery(url);
                const unwrapped = unwrapApiPayload(result);
                if (unwrapped.error) return unwrapped;
                return {
                    data: normalizeSourceTypeActionsData(unwrapped.data),
                    meta: unwrapped.meta,
                };
            },
            transformErrorResponse: handleErrorResponse,
        }),
        getMlEndPoints: builder.query<any, void>({
            query: () => apiEndPoints?.getMlEndPoints,
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiWorkersUsageByCategory: builder.mutation<any, any>({
            query: (payload) => ({
                url: apiEndPoints?.getAiWorkersUsageByCategory,
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiworkersPresignedUrl: builder.mutation<any, any>({
            queryFn: async (payLoad, api, _extra, baseQuery) => {
                const state = api.getState() as RootState;
                const currentToken = resolveClaimIntakeBearerToken(state)?.trim() ?? '';
                if (currentToken) {
                    // Same revival path as start-stream / dify_stream (Auth0 or guest silent refresh).
                    await getValidClaimIntakeBearerToken(currentToken, {
                        getState: () => api.getState() as RootState,
                        dispatch: api.dispatch,
                    });
                }

                const base = {
                    url: apiEndPoints.getAidesPresignedUrl,
                    method: 'POST' as const,
                    body: payLoad,
                };
                const useRest = shouldUseClaimIntakeRestPaths(api.getState() as RootState);
                const result = useRest
                    ? await baseQuery({
                          ...base,
                          url: toClaimIntakeApiPath(apiEndPoints.getAidesPresignedUrl, true),
                          headers: buildClaimIntakeRequestHeaders(api.getState),
                      })
                    : await baseQuery(base);
                return unwrapApiPayload(result);
            },
            transformErrorResponse: handleErrorResponse,
        }),
        getDifyWorkflows: builder.query<any, void>({
            query: () => '/api/v1/dify/workflows',
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiWorkerUsageById: builder.mutation<any, string>({
            query: (usageId) => ({
                url: `${apiEndPoints.getAiWorkersUsageByCategory}/${usageId}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getTemplateUsagePdf: builder.query<{ presigned_url: string }, { aiworker_usage_id: string; aiworker_id: string }>({
            query: ({ aiworker_usage_id, aiworker_id }) => ({
                url: `${apiEndPoints.getTemplateUsagePdf}/${aiworker_usage_id}/pdf?aiworker_id=${encodeURIComponent(aiworker_id)}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response?.data ?? {},
            transformErrorResponse: handleErrorResponse,
        }),
        getTemplateUsageHtml: builder.query<any, { aiworker_usage_id: string; aiworker_id: string }>({
            query: ({ aiworker_usage_id, aiworker_id }) => ({
                url: `${apiEndPoints.getTemplateUsagePdf}/${aiworker_usage_id}/html?aiworker_id=${encodeURIComponent(aiworker_id)}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response?.data ?? {},
            transformErrorResponse: handleErrorResponse,
        }),
        getAgentHisotry: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: apiEndPoints.getAgentHisotry,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        exportAiWorkerUsage: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: apiEndPoints.exportAiWorkerUsage,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        updateOntology: builder.mutation<any, any>({
            query: (payload) => ({
                url: apiEndPoints.updateOntology,
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        saveAiWorkerUsage: builder.mutation<any, { aiworker_id: string; payLoad: any }>({
            query: ({ aiworker_id, payLoad }) => ({
                url: `${apiEndPoints.updateAiWorkers}/${aiworker_id}/usage`,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiworkersPresignedUrlListKey: builder.mutation<any, { base_prefix: string }>({
            query: (payLoad) => ({
                url: apiEndPoints.getPresignedUrlListKey,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiworkersPresignedUrlByKey: builder.mutation<any, { s3_key: string }>({
            query: (payLoad) => ({
                url: apiEndPoints.getPresignedUrlViewByKey,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        deployPublicEndpoint: builder.mutation<
            any,
            { aiworker_id: string; is_enabled: boolean; metadata: Record<string, unknown> }
        >({
            query: (payLoad) => ({
                url: apiEndPoints.deployPublicEndpoint,
                method: 'PATCH',
                body: payLoad
            }),
            transformErrorResponse: handleErrorResponse,
        }),
    })
});

export const { useGetAiWorkersByCategoryQuery, useLazyGetCommandSourceRecordsQuery, useExecuteCommandRequestMutation, useExecuteCommandIntegrationMutation, useFetchAiWorkersByCategoryMutation,
    useUpdateAiWorkersMutation, useCreateAiWorkersMutation,
    useGetAiWorkerByIdQuery, useLazyGetAiWorkerByIdQuery, useDeleteAiWorkersMutation, useCircuitConnectorMutation, useGetAiWorkersBySourceTypeQuery, useGetMlEndPointsQuery, useGetAiWorkersUsageByCategoryMutation, useGetAiworkersPresignedUrlMutation, useGetAiWorkerUsageByIdMutation, useLazyGetTemplateUsagePdfQuery, useLazyGetTemplateUsageHtmlQuery, useGetAgentHisotryMutation, useExportAiWorkerUsageMutation, useUpdateOntologyMutation, useGetDifyWorkflowsQuery, useSaveAiWorkerUsageMutation, useDeployPublicEndpointMutation, useGetAiworkersPresignedUrlListKeyMutation, useGetAiworkersPresignedUrlByKeyMutation} = aidesApiSlice;
